import { CommonModule } from '@angular/common';
import { Component, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';

import {
  Client,
  CommandQueryDataIn,
  CommandStatus,
  ConnectDisconnectDataOut,
  PageInfo,
  SortingType,
} from '../../../api/api-reference';
import { PermissionsEnum } from '../../../api/api-reference';
import { IsNewDirective } from '../../../directives/is-new.directive';
import { AuthService } from '../../../services/auth.service';
import { SignalRService } from '../../../services/signalr.service';
import { DatepickerComponent } from '../../core/components/datepicker/datepicker.component';
import { SearchComponent } from '../../core/components/search/search.component';
import { SortComponent } from '../../core/components/sort/sort.component';
import { StatusComponent } from '../../core/components/status/status.component';
import { DateRangeInput } from '../../core/models/date-range-input';
import { PaginationComponent } from '../../core/components/pagination/pagination.component';
import { ConnectDisconnectInformationComponent } from './connect-disconnect-information/connect-disconnect-information.component';
import { ClipboardService } from '../../../services/clipboard.service';
import { SignalRBaseComponent } from '../../../services/signalr-base.component';
import { RelayStateService } from '../../../services/relay-state.service';
import { processDateRange } from '../../../helper/date-utils';

@Component({
  selector: 'app-connect-disconnect',
  standalone: true,
  imports: [
    TranslateModule,
    CommonModule,
    RouterLink,
    SortComponent,
    DatepickerComponent,
    SearchComponent,
    PaginationComponent,
    IsNewDirective,
    ConnectDisconnectInformationComponent,
    StatusComponent,
    AngularSvgIconModule,
  ],
  templateUrl: './connect-disconnect.component.html',
  styleUrl: './connect-disconnect.component.scss',
})
export class ConnectDisconnectComponent extends SignalRBaseComponent {
  public pageSize: number = 10;
  public pageNumber: number = 1;
  public currentPage: number = 1;
  public numberOfCommands: number = 0;
  public lastPageSize: number = 1;
  public selectedId: string | undefined | null;
  public PermissionsEnum = PermissionsEnum;

  data: ConnectDisconnectDataOut[] | undefined = [];
  readonly dayInMs = 24 * 60 * 60 * 1000;
  isDatepickerOpen = false;
  isModalOpen: boolean = false;

  requestData = new CommandQueryDataIn({
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: this.pageNumber }),
    search: undefined,
    startDate: undefined,
    endDate: undefined,
    sorting: SortingType.Descending,
    status: undefined,
  });

  commandStatuses = Object.values(CommandStatus).map(s => s.toUpperCase());

  private relaySubscription?: Subscription;

  constructor(
    private client: Client,
    private toastr: ToastrService,
    public authService: AuthService,
    signalRService: SignalRService,
    public clipboardService: ClipboardService,
    private relayState: RelayStateService,
  ) {
    super(signalRService);
    this.reloadTable(1);
  }

  override async ngOnDestroy(): Promise<void> {
    this.relaySubscription?.unsubscribe();
    await super.ngOnDestroy();
  }

  protected override registerHandlers(): void {
    // Suppress warnings for the 'online-status' broadcast
    this.registerSignalREvent('online-status', () => {});
  }

  override async ngOnInit(): Promise<void> {
    await super.ngOnInit();

    // 'disconnect-control' is owned by RelayStateService, so the row update it used to do
    // inline in registerHandlers happens here instead.
    void this.relayState.ensureSubscribed();
    this.relaySubscription = this.relayState.results$.subscribe((message) => {
      const item = this.data?.find(
        (item) => item.commandId === message.commandId,
      );
      if (item) {
        item.status = message.commandStatus;
        if (message.commandStatus == CommandStatus.Failed || message.commandStatus == CommandStatus.ErrorOccured) {
          try {
            const parsed = JSON.parse(message.errorMessage!);
            item.errorMessage = parsed.Message;
          } catch {
            item.errorMessage = message.errorMessage;
          }
        }
      }
    });
  }
  reloadTable(page?: number) {
    this.pageNumber = page || 0;
    if (page) this.requestData!.pageInfo!.page = this.pageNumber;
    this.selectedId = undefined;
    this.client.setConnectedStateCommandQuery(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data;
        this.numberOfCommands = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfCommands / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });

    this.selectedId = null;
  }

  searchChange(searchText: string) {
    this.requestData.search = searchText;
    this.reloadTable(1);
  }

  printResponseOfCommand(event: MouseEvent, cmd: ConnectDisconnectDataOut) {
    if (cmd.status === CommandStatus.Failed) {
      event.stopPropagation();
      this.toastr.error(cmd.errorMessage);
    }
  }

  retryCommand(cmd: ConnectDisconnectDataOut, event: MouseEvent) {
    event.stopPropagation();
    this.client.onDemandConnectDisconnectRetry(cmd.commandId!).subscribe({
      next: () => {
        this.toastr.success('Command retry initiated successfully');
        this.reloadTable(this.currentPage);
      },
      error: (err: string) => {
        this.toastr.error('Failed to retry command: ' + err);
      },
    });
  }


  pageChange(value: number) {
    this.currentPage = value;
    this.reloadTable(this.currentPage);
  }

  onSortOption(selectedSort?: SortingType) {
    if (selectedSort) this.requestData.sorting = selectedSort;
    this.reloadTable();
  }

  onStatusOption(selectedStatus?: string) {
    if (selectedStatus !== undefined) {
      this.requestData.status =
        selectedStatus === 'ALL'
          ? undefined
          : (selectedStatus as CommandStatus);
    }
    this.reloadTable();
  }

  ensureDate(dateTime: unknown): Date | null {
    return dateTime instanceof Date ? dateTime : null;
  }

  selectedDate: string | null = '';
  onDateRangeChanged(input: DateRangeInput): void {
    const result = processDateRange(input);

    this.requestData.startDate = result.startDate;
    this.requestData.endDate = result.endDate;
    this.selectedDate = result.selectedDate;
    this.filterDatesInTable();
  }

  clearDates(): void {
    this.requestData.startDate = undefined;
    this.requestData.endDate = undefined;
    this.reloadTable();
  }

  toggleModal() {
    this.isModalOpen = !this.isModalOpen;
  }

  toggleDatepicker() {
    this.isDatepickerOpen = !this.isDatepickerOpen;
  }

  handleRowClick(id: string) {
    if (!this.isModalOpen) {
      this.selectedId = id;
      this.isModalOpen = true;
    }
  }

  filterDatesInTable() {
    this.client.setConnectedStateCommandQuery(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data;
        this.numberOfCommands = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfCommands / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }
}
